export interface TenantOption {
    /** Display name in the picker. */
    name: string;
    /**
     * Hostname label that selects this tenant, e.g. `gearnest` matches
     * `gearnest.uatapps.performx.dev`. See {@link resolveTenantFromHostname}.
     */
    slug: string;
    tenantId: string;
}

/**
 * Tenant ids by name, so an app config can gate itself to a tenant without
 * repeating a uuid in a second place. See `UatAppConfig.tenants`.
 */
export const TENANT_IDS = {
    gearNest: 'aaaaaaaa-331e-410e-9221-939a2fe74d9e',
    mockAuto: 'aaaaaaaa-abcd-410e-9221-939a2fe74d9e',
    northWind: 'e6d5ecd2-abcd-410e-9221-939a2fe74d9e',
} as const;

/**
 * The dev tenants these apps run against.
 *
 * Hardcoded on purpose: this is a nonprod tool, the list is short, and fetching it
 * would need a token before the user has chosen the tenant that token is scoped to.
 */
export const TENANT_OPTIONS: TenantOption[] = [
    { name: 'Gear Nest', slug: 'gearnest', tenantId: TENANT_IDS.gearNest },
    {
        name: 'Mock Automotive Sales Data',
        slug: 'mockauto',
        tenantId: TENANT_IDS.mockAuto,
    },
    { name: 'North Wind', slug: 'northwind', tenantId: TENANT_IDS.northWind },
];

/**
 * The tenant a hostname names, or null.
 *
 * This is the seam for per-tenant subdomains (`gearnest.uatapps.performx.dev`).
 * Today every deployment serves one host, so this returns null and the picker is
 * the only way in. Standing up tenant subdomains later is an ingress and Auth0
 * change with no application code: add the host, and this function starts matching.
 *
 * Note it only *preselects*. The picker stays available so a tester can switch
 * tenants without retyping the URL, and so an unrecognised hostname degrades to the
 * normal flow rather than dead-ending.
 *
 * Wildcard TLS is the current blocker for adding hosts freely: both ClusterIssuers
 * use HTTP-01, which cannot issue a wildcard certificate, so each tenant host needs
 * its own enumerated ingress entry until a DNS-01 solver exists.
 */
export function resolveTenantFromHostname(
    hostname: string = window.location.hostname,
): TenantOption | null {
    const label = hostname.split('.')[0]?.toLowerCase();
    if (!label) return null;

    return TENANT_OPTIONS.find((tenant) => tenant.slug === label) ?? null;
}
